import { DIAMETERS, EDITOR_TOOLS } from '@editor/config/constants'
import { PLANE_OPACITY } from '@editor/config/styles'
import { EditorTool } from '@editor/interfaces/editor'
import { IntervalsConfig } from '@editor/interfaces/inspectionItemGrid'
import {
  CuboidAnchor,
  Cylinder,
  InspectionItem,
  Polygon,
  Shape,
  ShapeKey,
  ShapeKeyType,
  Shapes,
  ShapesId,
} from '@editor/interfaces/interfaces'
import { PT_TO_MM } from '@editor/pages/projects/blueprint-editor/definitions'
import * as Sentry from '@sentry/react'
import { isEqual } from 'lodash'
import { Color } from 'three'

import { ERROR_PROCESS, getCommonErrorMessage, getErrorText, isErrorBody } from './ErrorHandler'
import { meterToMillimeter, roundNumber } from './Math'
import AbortError from './errors/AbortError'
import { GenericError } from './errors/GenericError'

export * from './Math'

/**
 * ファイル拡張子からContentTypeを返す
 * @param {string} fileName ファイル名
 * @return {string | null} 拡張子もしくはnull
 */
export const returnContentType = (fileName: string): string | null => {
  const extension: string | undefined = fileName.split('.').pop()
  const allowedExtensions = new Set<string | undefined>(['las', 'laz', 'ply'])
  if (allowedExtensions.has(extension)) return 'text/plain'
  return null
}

/**
 * Parse point count from PCD file header (LAS/LAZ/PLY)
 * @param file - The file to parse
 * @param extension - File extension (las/laz/ply)
 * @returns The point count or null if parsing fails
 */
export const getPointCount = async (file: File, extension: string): Promise<number | null> => {
  try {
    if (extension === 'las' || extension === 'laz') {
      // LAS/LAZ: Read first ~260 bytes for header
      const headerBuffer = await file.slice(0, 260).arrayBuffer()
      const dataView = new DataView(headerBuffer)

      // Verify LASF signature at offset 0
      const signature = String.fromCharCode(
        dataView.getUint8(0),
        dataView.getUint8(1),
        dataView.getUint8(2),
        dataView.getUint8(3),
      )
      if (signature !== 'LASF') {
        return null
      }

      // Read version major/minor at bytes 24-25
      const versionMajor = dataView.getUint8(24)
      const versionMinor = dataView.getUint8(25)

      // Read legacy point count (uint32 little-endian) at offset 107
      const legacyPointCount = dataView.getUint32(107, true)

      // If version >= 1.4 and legacy count is 0, read 64-bit count at offset 247
      if (((versionMajor === 1 && versionMinor >= 4) || versionMajor > 1) && legacyPointCount === 0) {
        // Read as two 32-bit values to handle 64-bit integer
        const lowBits = dataView.getUint32(247, true)
        const highBits = dataView.getUint32(251, true)
        // Combine into a number (may lose precision for very large values, but 85M is well within safe range)
        return highBits * 0x100000000 + lowBits
      }

      return legacyPointCount
    }

    if (extension === 'ply') {
      // PLY: Read first ~4KB as text to find header
      const headerBlob = await file.slice(0, 4096).arrayBuffer()
      const headerText = new TextDecoder('utf-8').decode(headerBlob)

      // Find "element vertex <number>" line
      const match = headerText.match(/element\s+vertex\s+(\d+)/i)
      if (match && match[1]) {
        return parseInt(match[1], 10)
      }

      return null
    }

    return null
  } catch (error) {
    Sentry.captureException(error)
    // If parsing fails, return null to allow upload to proceed
    return null
  }
}

/**
 * Check if a value is defined when a number field can be null or undefined and 0 is a valid value.
 *
 * @param value
 */
export function isValueDefined<T = number>(value: T | null | undefined): value is T {
  return value !== undefined && value !== null
}

/**
 * Get the distance label with unit and optional width
 * @param distance in meters
 * @param width in millimeters (optional)
 * @returns label with unit and width if provided
 */
export const getDistanceLabel = (distance: number, width?: number | null): string => {
  const roundedDistance = roundNumber(distance, '0.001')
  const distanceInMillimeter = meterToMillimeter(roundedDistance)

  let lengthLabel: string
  if (distanceInMillimeter < 10000) {
    lengthLabel = `${distanceInMillimeter}mm`
  } else {
    lengthLabel = `${roundedDistance}m`
  }

  // Only add width if it's explicitly set (excluding 0)
  if (width !== undefined && width !== null && width !== 0) {
    return `${lengthLabel} × ${width}mm`
  }

  return lengthLabel
}

//* Used for Diameter dropdown
export const findDiameterKeyByValue = (diameterValue: number | undefined) =>
  Object.keys(DIAMETERS).find((key) => DIAMETERS[key] === diameterValue) || (diameterValue || '').toString()

export const shapesExist = (shapes: Shapes): boolean => !!shapes.cylinders.length || !!shapes.polygons.length

export const validateEmail = (email: string) => {
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/
  return emailRegex.test(email)
}

//* Used for checking clearing guidelines before switching tool
export const needClearAnchorFrames = (
  newTool: string,
  cuboidAnchor: CuboidAnchor | undefined,
  intervals: Record<string, IntervalsConfig>,
  toolVolumePolygonIsDirty: boolean,
  toolRebarDetectionIsDirty: boolean,
  toolPlaneDetectionIsDirty: boolean,
  toolPolylineIsDirty: boolean,
  toolCameraProfileIsDirty: boolean,
) => {
  // no need to clear guidelines if user switches to the tools that not modify the points
  if (newTool === EDITOR_TOOLS.COMMENT || newTool === EDITOR_TOOLS.FOCUS) {
    return false
  }

  // TODO: Need to figure out how to do this modularized way.

  if (newTool !== EDITOR_TOOLS.PCD_TRIM_CUBOID && cuboidAnchor) {
    return true
  }

  if (newTool !== EDITOR_TOOLS.CYLINDER && toolRebarDetectionIsDirty) {
    return true
  }

  if (newTool !== EDITOR_TOOLS.PLANE && toolPlaneDetectionIsDirty) {
    return true
  }

  if (newTool !== EDITOR_TOOLS.POLYLINE && toolPolylineIsDirty) {
    return true
  }

  if (newTool !== EDITOR_TOOLS.GRID && Object.keys(intervals).length > 0) {
    return true
  }

  if (newTool !== EDITOR_TOOLS.VOLUME_POLYGON && toolVolumePolygonIsDirty) {
    return true
  }

  if (newTool !== EDITOR_TOOLS.CAMERA_PROFILE && toolCameraProfileIsDirty) {
    return true
  }

  return false
}

/**
 * Convert array of shape IDs to ShapesId instance.
 * @param shapes Shapes reference.
 * @param shapeIds Shape IDs to be converted.
 * @returns
 */
export const shapeIdArrayToShapeIds = (shapes: Shapes, shapeIds: string[]): ShapesId => ({
  cylinders: shapes.cylinders.filter((c) => shapeIds.includes(c.shape_id)).map((c) => c.shape_id),
  polygons: shapes.polygons.filter((t) => shapeIds.includes(t.shape_id)).map((t) => t.shape_id),
})

/**
 * Check if a shape can be selected
 * @param shape Shape to be selected
 * @throws Error if shape cannot be selected
 */
export const validateSelectedShape = (
  shape: Shape,
  shapeKey: ShapeKey | ShapeKeyType,
  inspectionItems: InspectionItem[],
  currentTool?: EditorTool,
) => {
  if (currentTool?.config?.volume?.selectable && shapeKey === ShapeKeyType.POLYGON) {
    if (!inspectionItems.some((item) => item.shape_ids.polygons.includes(shape.shape_id))) {
      throw new Error('体積測定に失敗した平面は選択できません。')
    }
  }
}

/**
 * Calculates the surface area of a rebar.
 * @param cylinder Cylinder
 * @returns The surface area of the rebar.
 */
export const calculateRebarSurfaceArea = (cylinder: Cylinder) => {
  const { diameter, length } = cylinder
  // 2πr^2 + 2πrh
  const radius = diameter / 2
  const surfaceArea = 2 * Math.PI * radius * radius + 2 * Math.PI * radius * length
  return surfaceArea
}

/**
 * Returns num evenly spaced samples, calculated over the interval [start, stop].
 *
 * @param start Start value
 * @param end End value
 * @param n Number of samples
 */
export const linspace = (start: number, end: number, n: number) => {
  if (n === 1) return [start]

  const step = (end - start) / (n - 1)
  return Array.from({ length: n }, (_, i) => start + step * i)
}

/**
 * Transposes a matrix represented as a flat array.
 * The original matrix is assumed to be in left-to-right, top-to-bottom order.
 * The transposed matrix will be in top-to-bottom, left-to-right order.
 *
 * @param matrix The flat array representing the matrix.
 * @param rows The number of rows in the matrix.
 * @param cols The number of columns in the matrix.
 * @returns A new array containing the transposed matrix, or throws an error if dimensions are invalid.
 *          If the matrix is not a rectangle, the original matrix is returned.
 */
export function transposeMatrix<T>(matrix: T[], rows: number, cols: number): T[] {
  if (!rows || !cols || rows * cols !== matrix.length) {
    return matrix
  }

  const transposedMatrix: T[] = new Array<T>(matrix.length)

  for (let i = 0; i < rows; i += 1) {
    for (let j = 0; j < cols; j += 1) {
      transposedMatrix[j * rows + i] = matrix[i * cols + j]
    }
  }

  return transposedMatrix
}

/**
 * Check if a shape is hovered or selected.
 *
 * @param shape Shape to be checked
 * @param hoveredShapeId Currently hovered shape ID
 * @param selectedShapeIds Currently selected shape IDs
 */
export const isHoverSelected = (shapeId: string, hoveredShapeId: string, selectedShapeIds: string[]) =>
  hoveredShapeId === shapeId || selectedShapeIds.includes(shapeId)

/**
 * Get the opacity of a plane based on its state.
 *
 * @param shape Plane or Polygon.
 * @param dimmedShapeIds Dimmed shape IDs.
 * @param hoveredShapeId Hovered shape ID.
 * @returns Opacity value
 */
export const getPlaneOpacity = (
  shape: Polygon,
  dimmedShapeIds: string[],
  isHovered: boolean,
  opacities: Record<keyof typeof PLANE_OPACITY, number> = PLANE_OPACITY,
): number => {
  if (dimmedShapeIds.includes(shape.shape_id)) return opacities.DIMMED
  if (isHovered) return opacities.HOVER
  return opacities.STANDARD
}

/**
 * Returns a hash code from a string
 * @param  str The string to hash.
 * @return     A 32bit integer
 * @see http://werxltd.com/wp/2010/05/13/javascript-implementation-of-javas-string-hashcode-method/
 */
export const hashCode = (str: string): number => {
  let hash = 0
  for (let i = 0, len = str.length; i < len; i += 1) {
    const chr = str.charCodeAt(i)
    hash = (hash << 5) - hash + chr // eslint-disable-line no-bitwise
    hash |= 0 // eslint-disable-line no-bitwise
  }
  return hash
}

/**
 * Extracts hash IDs from a string.
 * @param input
 * @returns
 */
export const extractHashIds = (input: string): Record<string, string> => {
  const ids = input.split('#').filter(Boolean)
  const result = {} as Record<string, string>

  ids.forEach((id) => {
    const separatorIndex = id.indexOf('-') // the first hyphen is our separator
    const key = id.substring(0, separatorIndex)
    const value = id.substring(separatorIndex + 1)
    result[key] = value
  })

  return result
}

/**
 * Creates a contained promise for easier error/completion handling when you have multiple
 * chained promises to be run but single completion or errors handler.
 *
 * @example
 * ```typescript
 * await withPromiseContained<{ result: Object }>(async () => {
 *   const result = await somePromise()
 *   if (!result) return
 *
 *   const result2 = await somePromise2()
 *   if (!result2) return
 *
 *   return result2
 * }).finally(() => cleanUp())
 * ```
 *
 * @param fn Promise to be contained
 * @returns
 */
export async function withPromiseContained<T>(fn: () => Promise<T>): Promise<T> {
  return fn()
}

/**
 * Get the unique string portion of an ID.
 *
 * eg: 2024-12-13T00:30:22.335Z:rez9b1kb ⇒ rez9b1kb
 *
 * @param id
 * @returns
 */
export function getIdUniqueString(id: string): string {
  return id.substring(id.length - 8) || ''
}

export const colorToRGB = (() => {
  const c = new Color()

  return (color: string) => {
    c.set(color)
    return c.toArray()
  }
})()

/**
 * Wraps an API function with standard success and error handlers.
 *
 * @param apiFunc The API function to wrap.
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function withStandardHandlers<TFunc extends (...args: any[]) => Promise<Response>, TResult>(
  apiFunc: TFunc,
  errorProcess: keyof typeof ERROR_PROCESS,
): (...args: Parameters<TFunc>) => Promise<TResult> {
  return async (...args: Parameters<TFunc>): Promise<TResult> => {
    const res = await apiFunc(...args)

    if (!res.ok) {
      const body = (await res.json()) as unknown
      if (body instanceof Object && isErrorBody(body)) {
        throw new GenericError(getErrorText(errorProcess, body.status_id))
      }

      throw new GenericError(getCommonErrorMessage(errorProcess))
    }

    if (res.status === 204) return {} as unknown as TResult // Return empty object for 204 No Content

    try {
      return (await res.json()) as TResult
    } catch (error) {
      Sentry.captureException(error)
      throw new GenericError(getCommonErrorMessage(errorProcess))
    }
  }
}

/**
 * Fetches a binary buffer from a URL with optional progress reporting.
 *
 * @param url URL to fetch the buffer from
 * @param onProgress Optional progress callback (0 to 1)
 */
export const fetchBufferWithProgress = async (
  url: string,
  signal?: AbortSignal,
  onProgress?: (progress: number) => void,
): Promise<ArrayBuffer> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('GET', url, true)
    xhr.responseType = 'arraybuffer'

    if (onProgress) {
      xhr.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(event.loaded / event.total)
        }
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response as ArrayBuffer)
      } else {
        reject(new Error(xhr.statusText || `HTTP ${xhr.status}`))
      }
    }

    xhr.onerror = () => reject(new Error('Network error'))
    xhr.onabort = () => reject(new AbortError('Request aborted', url))

    if (signal) {
      signal.addEventListener('abort', () => {
        xhr.abort()
      })
    }

    xhr.send()
  })

// Scale calculation constants and functions
export const TEXT_ANNOTATION_THICKNESS_NORMALIZATION = 35.2777
export const MINIMUM_PRINT_SCALE = 0.0001

/**
 * Creates a safe print scale value to avoid division by zero
 */
export const getSafePrintScale = (printScale: number): number => Math.max(printScale, MINIMUM_PRINT_SCALE)

/**
 * Convert print dimensions to Three.js world dimensions
 * Used by all drawing tools for converting print dimensions to render dimensions
 *
 * @param printDimension - The dimension value in print units (points)
 * @param printScale - Current print scale factor
 * @param options - Configuration options
 * @returns Calculated dimension in meters for Three.js rendering
 */
export const convertPrintDimension = (
  printDimension: number,
  printScale: number,
  options: {
    ensureMinimum?: boolean // Whether to apply Math.max with minimum dimension (consistent visuals)
  } = {},
): number => {
  const { ensureMinimum = true } = options
  const safePrintScale = getSafePrintScale(printScale)

  const worldDimension = printDimension * PT_TO_MM * (1 / safePrintScale)

  if (ensureMinimum) {
    // Ensure minimum visible dimension while maintaining scale responsiveness
    return Math.max(worldDimension, PT_TO_MM * (1 / safePrintScale)) / 1000
  }

  return worldDimension / 1000
}

/**
 * Convert millimeters to points considering print scale for UI display.
 * This is used to show mm values as points in the UI while keeping storage in mm.
 *
 * @param mm - Value in millimeters (stored value)
 * @param printScale - Current print scale factor
 * @returns Value in points for display (integer)
 */
export const mmToPointsWithScale = (mm: number, printScale: number): number => {
  const safePrintScale = getSafePrintScale(printScale)
  // Convert mm to points: mm / (PT_TO_MM * (1 / printScale))
  return Math.round(mm / (PT_TO_MM * (1 / safePrintScale)))
}

/**
 * Convert points to millimeters considering print scale for storage.
 * This is used to convert display points back to mm for storage.
 *
 * @param points - Value in points (display value)
 * @param printScale - Current print scale factor
 * @returns Value in millimeters for storage
 */
export const pointsToMmWithScale = (points: number, printScale: number): number => {
  const safePrintScale = getSafePrintScale(printScale)
  // Convert points to mm: points * PT_TO_MM * (1 / printScale)
  return points * PT_TO_MM * (1 / safePrintScale)
}

/**
 * Returns an object containing only the properties where values differ between old and new.
 */
export const diffProps = (oldObj: Record<string, unknown>, newObj: Record<string, unknown>): Record<string, unknown> =>
  Object.keys({ ...oldObj, ...newObj }).reduce((diff, key) => {
    if (!isEqual(oldObj[key], newObj[key])) {
      return { ...diff, [key]: newObj[key] }
    }
    return diff
  }, {})
